import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import LocalizationPanel, { LocalizationChangeHandler } from './LocalizationContainer';
import { TargetNode } from '../../models/TargetNode';


type ChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => void;

// One follow-up question: its current text plus the two handlers that edit the
// lead language and the other localizations.
interface FeedbackQuestion {
  value: string;
  onChange: ChangeHandler;
  onLocalizationChange: LocalizationChangeHandler;
}

interface AdditionalQuestionsPanelOwnProps {
  node: TargetNode;
  // Whether the follow-up questions are switched on (extra.c.c45).
  enabled: boolean;
  // Topic buttons force these on and hide the toggle, so the checkbox reads
  // `enabled || topicButtonsEnabled` and the label/input are hidden.
  topicButtonsEnabled: boolean;
  onToggleEnabled: ChangeHandler;
  positive: FeedbackQuestion;
  negative: FeedbackQuestion;
  neutral: FeedbackQuestion;
}

// Static per-row metadata. `index` is the slot in the question array that
// LocalizationPanel edits — note the neutral question is 5, not 3 (3 is the
// contact-form title, 4 the thank-you text).
const ROWS: Array<{
  key: keyof Pick<AdditionalQuestionsPanelOwnProps, 'positive' | 'negative' | 'neutral'>;
  label: string;
  name: string;
  redisid: string;
  index: number;
}> = [
  { key: 'positive', label: 'target.editor.label.feedbackQuestionPositive', name: 'contactFormFeedbackQuestionIfPlus', redisid: 'feedbackquestionifplus', index: 1 },
  { key: 'negative', label: 'target.editor.label.feedbackQuestionNegative', name: 'contactFormFeedbackQuestionIfMinus', redisid: 'feedbackquestionifminus', index: 2 },
  { key: 'neutral', label: 'target.editor.label.feedbackQuestionNeutral', name: 'contactFormFeedbackQuestionIfNeutral', redisid: 'feedbackquestionifneutral', index: 5 },
];

const AdditionalQuestionsPanel = (
  props: AdditionalQuestionsPanelOwnProps & WithTranslation
) => {
  const { node, enabled, topicButtonsEnabled, onToggleEnabled, t } = props;
  const hiddenWhenTopicButtons = topicButtonsEnabled ? "hidden" : "";

  return (
    <fieldset>
      <legend>{t('target.editor.label.additionalQuestions')}</legend>
      <label className={hiddenWhenTopicButtons}>{t('target.editor.label.enable')}</label>
      <input className={hiddenWhenTopicButtons} type="checkbox" data-redisid="showoptionalquestions"
        title="Enable Optional Questions"
        checked={enabled || topicButtonsEnabled}
        onChange={onToggleEnabled}
      />
      <div style={{ height: "0.2em" }}></div>
      <div className={!enabled ? "hidden" : ""}>
        {ROWS.map(({ key, label, name, redisid, index }) => {
          const question = props[key];
          return (
            <React.Fragment key={redisid}>
              <label>{t(label)}</label>
              <input type="text" name={name}
                data-redisid={redisid}
                value={question.value}
                onChange={question.onChange}
              />
              <LocalizationPanel
                node={node}
                localizationChanged={question.onLocalizationChange}
                header={t('target.editor.button.addQuestionLocalisation')}
                localizationIndex={index}
              />
            </React.Fragment>
          );
        })}
      </div>
    </fieldset>
  );
};

export default withTranslation('common')(AdditionalQuestionsPanel);
